import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";

interface TenancyDoc {
  _id: string;
  filename: string;
  description: string | null;
  uploadedAt: number;
  url: string | null;
}

// Landlord-side viewer for the personal docs a tenant has uploaded
// against a specific tenancy. Read-mostly; landlord can delete a
// doc if it needs to come down (e.g. the tenant left and the file
// is sensitive). Collapsed by default to keep the lease card tidy
// when no one has uploaded anything.
export function TenancyDocsViewer({ tenancyId }: { tenancyId: string }) {
  const [open, setOpen] = useState(false);
  const docs = useQuery(
    anyApi.documents.docsForTenancy,
    open ? { tenancyId } : "skip",
  ) as TenancyDoc[] | undefined;
  const remove = useMutation(anyApi.documents.remove);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRemove = async (docId: string, filename: string) => {
    if (!confirm(`Delete "${filename}"? This can't be undone.`)) return;
    setBusyId(docId);
    setError(null);
    try {
      await remove({ docId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] font-medium text-slate-500 hover:text-slate-900 transition-colors"
      >
        {open ? "Hide" : "Show"} personal docs{" "}
        {docs && open && docs.length > 0 ? `(${docs.length})` : ""}
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/50 p-3">
          <Body
            docs={docs}
            error={error}
            busyId={busyId}
            onRemove={handleRemove}
          />
        </div>
      )}
    </div>
  );
}

function Body({
  docs,
  error,
  busyId,
  onRemove,
}: {
  docs: TenancyDoc[] | undefined;
  error: string | null;
  busyId: string | null;
  onRemove: (id: string, filename: string) => void;
}) {
  if (docs === undefined) {
    return <p className="text-xs text-slate-500">Loading…</p>;
  }
  if (docs.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        Nothing uploaded yet.
      </p>
    );
  }
  return (
    <>
      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
      <ul className="divide-y divide-slate-200">
        {docs.map((d) => (
          <li
            key={d._id}
            className="py-2 flex items-center justify-between gap-3 first:pt-0"
          >
            <div className="min-w-0 flex-1">
              {d.url ? (
                <a
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-slate-900 hover:underline truncate block"
                >
                  {d.filename}
                </a>
              ) : (
                <span className="text-sm text-slate-500 truncate block">
                  {d.filename}
                </span>
              )}
              <p className="text-[11px] text-slate-500">
                Uploaded {formatTimestamp(d.uploadedAt)}
                {d.description ? ` · ${d.description}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRemove(d._id, d.filename)}
              disabled={busyId === d._id}
              className="shrink-0 text-[11px] font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-0.5 rounded hover:bg-slate-100 disabled:opacity-50"
            >
              {busyId === d._id ? "…" : "Delete"}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
